/**
 * One card on the pin board. Face is title + caption + chart (`ViewCard`, shared with the chat
 * rail); everything else — a template's subtitle/lens note, callouts, the basis line, and the
 * "from conversation" link — lives in `ViewCard`'s click-to-open popup. The unpin control sits
 * outside that click target, absolutely positioned on top so it's never part of the gesture the
 * popup or the grid's own drag handling reads.
 */

import { Loader2, MessageCircle, X } from "lucide-react";
import { Link } from "react-router";

import { ViewCard } from "@/features/views/view-card";
import type { PinRecord } from "@/lib/api";

interface PinCardProps {
  pin: PinRecord;
  onUnpin: () => void;
  unpinning: boolean;
}

export function PinCard({ pin, onUnpin, unpinning }: PinCardProps) {
  return (
    <div className="group border-border bg-card relative flex h-full w-full flex-col overflow-hidden rounded-2xl border p-3">
      <button
        type="button"
        aria-label="Unpin view"
        title="Unpin"
        data-no-drag
        disabled={unpinning}
        className="text-muted-foreground hover:text-destructive bg-card/90 absolute top-2 right-2 z-10 rounded-full p-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 disabled:opacity-50"
        onClick={onUnpin}
      >
        {unpinning ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <X className="h-3.5 w-3.5" />
        )}
      </button>
      <ViewCard
        spec={pin.view.spec}
        id={pin.view.id}
        className="min-h-0 min-w-0 flex-1 cursor-pointer pr-9"
        footer={
          pin.view.conversation_id ? (
            // Deep link, not just "/chat": the point of the link is the conversation this view
            // came out of, which the chat page opens from `?c=` (see `ChatPage`).
            <Link
              to={`/chat?c=${encodeURIComponent(pin.view.conversation_id)}`}
              className="text-primary mt-1 inline-flex shrink-0 items-center gap-1 text-xs underline"
            >
              <MessageCircle className="h-3.5 w-3.5" /> from conversation
            </Link>
          ) : null
        }
      />
    </div>
  );
}
